// Display derivation for support tickets (Story #4613).
// The backend row stores only status=open|closed + close_reason; the finer
// display states (New / In progress / Waiting on you / Resolved / Closed) shown
// in the mockups are derived here so the whole app agrees on one mapping.

import { DISPLAY_STATUS_META, type DisplayStatus } from '../constants/support'
import type { SupportEvent, SupportTicket } from '../types/support'

const AEDT_TZ = 'Australia/Sydney'

/** Public, non-system thread events in chronological order. */
export function sortedEvents(events: SupportEvent[] | undefined): SupportEvent[] {
  return [...(events ?? [])].sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0))
}

const REQUESTER_EVENT_TYPES = new Set(['ticket_created', 'requester_message'])

/** Who authored a thread event, from the requester's point of view. */
export function eventAuthor(
  event: SupportEvent,
  requesterUserId: string | undefined,
): 'you' | 'support' | 'system' {
  if (event.event_type === 'ticket_closed' || event.event_type === 'ticket_reopened') return 'system'
  if (requesterUserId && event.actor === requesterUserId) return 'you'
  if (REQUESTER_EVENT_TYPES.has(event.event_type)) return 'you'
  return 'support'
}

/**
 * Derive the display status shown in chips/banners. Uses events when present
 * (detail read); falls back to created-vs-updated heuristics for list rows,
 * which carry no events.
 */
export function deriveDisplayStatus(ticket: SupportTicket, requesterUserId?: string): DisplayStatus {
  if (ticket.status === 'closed') {
    return ticket.close_reason === 'resolved' ? 'resolved' : 'closed'
  }
  const events = sortedEvents(ticket.events)
  // On the detail read we know who spoke last.
  const conversational = events.filter(
    (e) => e.event_type === 'requester_message' || eventAuthor(e, requesterUserId) === 'support',
  )
  if (conversational.length > 0) {
    const last = conversational[conversational.length - 1]
    return eventAuthor(last, requesterUserId) === 'support' ? 'waiting' : 'in_progress'
  }
  // List row (no events): brand-new when it has never been touched.
  if (ticket.updated_at && ticket.created_at && ticket.updated_at === ticket.created_at) return 'new'
  return events.length <= 1 ? 'new' : 'in_progress'
}

export function displayStatusMeta(status: DisplayStatus) {
  return DISPLAY_STATUS_META[status]
}

// ── Time formatting — always AEDT, matching the mockups ─────────────────────

function isToday(d: Date): boolean {
  const now = new Date()
  const fmt = (x: Date) =>
    new Intl.DateTimeFormat('en-AU', { timeZone: AEDT_TZ, year: 'numeric', month: '2-digit', day: '2-digit' }).format(x)
  return fmt(d) === fmt(now)
}

/** "Today, 2:14 pm" / "16 Jul, 9:41 am" — the mockup's last-update format. */
export function formatAedt(iso: string | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const time = new Intl.DateTimeFormat('en-AU', {
    timeZone: AEDT_TZ,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
    .format(d)
    .toLowerCase()
  if (isToday(d)) return `Today, ${time}`
  const day = new Intl.DateTimeFormat('en-AU', { timeZone: AEDT_TZ, day: 'numeric', month: 'short' }).format(d)
  return `${day}, ${time}`
}

/** Compact relative age ("2 h", "1 d", "4 d") for the admin queue Age column. */
export function relativeAge(iso: string | undefined): string {
  if (!iso) return '—'
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return '—'
  const mins = Math.max(0, Math.floor((Date.now() - then) / 60000))
  if (mins < 60) return `${mins} m`
  const hours = Math.floor(mins / 60)
  if (hours < 48) return `${hours} h`
  return `${Math.floor(hours / 24)} d`
}

export function formatBytes(bytes: number | undefined): string {
  if (!bytes || bytes <= 0) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
